import { parseISO } from 'date-fns/parseISO'
import { toUtcWallClock } from 'lib/helpers/toUtcWallClock/toUtcWallClock'

/**
 * Parses an ISO-formatted date string into a JavaScript `Date` object.
 *
 * @param date - The ISO-formatted date string to parse.
 * @returns The parsed `Date` object, or `null` if the provided date string is empty or invalid.
 *
 * @remarks
 * If the parsed date string does not contain a time zone offset, the function will return
 * a `Date` object representing the date and time in the UTC time zone. If the parsed date string
 * contains a time zone offset, the function will return a `Date` object representing the date
 * and time in the corresponding time zone.
 */
export const parseDateFromISO = (date?: Maybe<string>): Date | null => {
  if (!date) {
    return null
  }

  try {
    const parsedDate = parseISO(date)
    const isDateValid = parsedDate instanceof Date && !isNaN(parsedDate.getTime())

    if (!isDateValid) {
      return null
    }

    const isISO8601Date = /^\d{4}-\d{2}-\d{2}$/.test(date)

    return isISO8601Date ? parsedDate : toUtcWallClock(parsedDate)
  } catch {
    return null
  }
}
